// The maintenance gate, as the client reads it (the release manager,
// docs/design/deployment.md §6.1).
//
// The gate is ONE document on the origin. nginx aliases the flag file at
// `/maintenance.json` while the gate stands, and answers 404 once it comes
// down, so the whole of the client's question is "does that document
// answer?".
//
// This module holds the question and nothing else. It touches no DOM: the
// cover is `ui/maintenance.ts`, and the page that shows it decides when to
// show it. `net/` reads the wire and the origin, and nothing here may
// import a module that draws.
//
// **The clock is the server's.** A device with a wrong clock would count
// down to the wrong instant, or show a window that is already over. The
// answer's `Date` header is the server's own second, so the probe reports
// `now` from it and the caller counts an offset from that rather than
// reading the device clock as truth. This is the R220 rule, and
// `client/public/maintenance.js` follows it on the static page.

import {
  MAINTENANCE_OPEN_BURST_SEC, MAINTENANCE_POLL_SEC,
  MAINTENANCE_RELOAD_SPREAD_MS, MAINTENANCE_RUNNING_LONG_GRACE_SEC,
} from '../gen/balance';
import { isObject, num, str } from './json';

/** What the gate document says while the window stands. */
export interface MaintenanceInfo {
  /** The unix second the window is planned to close. 0 when it named none. */
  until: number;
  /** The operator's line for the player, or the empty string. */
  note: string;
  /** The SERVER's own unix second, off the answer's `Date` header. */
  now: number;
}

/**
 * Asks the gate document once.
 *
 * It answers the window while the gate stands, and `null` for every other
 * outcome: a 404, because that is the gate coming down; a 5xx, a network
 * fault or a body that is not a JSON object, because nothing said the gate
 * stands. A caller that must tell
 * "down" from "no answer" asks twice — `fast/main.ts` does, because a
 * server that is restarting answers neither.
 *
 * `cache: 'no-store'`, because the document is a flag: a cached 200 would
 * hold a window open after it closed.
 */
export async function probe(): Promise<MaintenanceInfo | null> {
  let res: Response;
  try {
    res = await fetch('/maintenance.json', { cache: 'no-store' });
  } catch {
    return null;
  }
  if (!res.ok) return null;
  let body: unknown;
  try {
    body = await res.json();
  } catch {
    return null;
  }
  // A body that is JSON but no object (`null`, a number, a list) says
  // nothing about a window, so it answers as no answer does.
  if (!isObject(body)) return null;
  return { until: num(body.until), note: str(body.note), now: serverNow(res) };
}

/**
 * The server's own unix second, from the answer's `Date` header. It falls
 * back to the device clock, which is the only thing left where a proxy
 * stripped the header: the countdown is then as right as the device is.
 */
function serverNow(res: Response): number {
  const stamp = res.headers.get('Date');
  if (stamp) {
    const t = Date.parse(stamp);
    if (!Number.isNaN(t)) return Math.floor(t / 1000);
  }
  return Math.floor(Date.now() / 1000);
}

// --- The planned end (R1191) -------------------------------------------------
//
// The gate comes down on the planned second (R1188), so a reader that
// waits the whole poll period after zero shows a closed game for up to ten
// seconds after it opened. The three rules below are the reader's half of
// the exact second. They are pure, so the cover and its test share them;
// `client/public/maintenance.js` follows the same three rules on the
// static page, with the figures from its data attributes.

/** What the end line shows for a span that is left. */
export type EndPhase = 'counting' | 'grace' | 'long';

/**
 * The phase of the countdown. Past zero the reader keeps the end line with
 * 0:00 for the grace, because the gate comes down in the first moments of
 * the planned second, and "the work runs long" would then be false. Only
 * a gate that still stands after the grace runs long.
 */
export function endPhase(
  leftSec: number, graceSec: number = MAINTENANCE_RUNNING_LONG_GRACE_SEC,
): EndPhase {
  if (leftSec > 0) return 'counting';
  return -leftSec < graceSec ? 'grace' : 'long';
}

/**
 * How long the reader waits before its next ask of the gate document. From
 * zero to the end of the burst it asks every second; at every other time it
 * asks on the page cadence. `leftSec` is null while no end is known.
 */
export function nextPollMs(
  leftSec: number | null,
  pollSec: number = MAINTENANCE_POLL_SEC,
  burstSec: number = MAINTENANCE_OPEN_BURST_SEC,
): number {
  if (leftSec !== null && leftSec <= 0 && -leftSec < burstSec) return 1000;
  return pollSec * 1000;
}

/**
 * The random pause before the reload that follows the 404. Every waiting
 * player meets the 404 in the same second, and the pause spreads their
 * `POST /api/join` calls over the spread rather than one millisecond.
 */
export function reloadDelayMs(
  random: () => number = Math.random, spreadMs: number = MAINTENANCE_RELOAD_SPREAD_MS,
): number {
  return Math.floor(random() * spreadMs);
}
